import React, { useState, useRef, useEffect } from "react";

interface EditableProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableDiv: React.FC<EditableProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [value, setValue] = useState(text);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.innerText;
    setValue(newValue);
    onEdit(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      divRef.current?.blur();
    }
  };

  return (
    <div
      ref={divRef}
      contentEditable
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`cursor-text outline-none ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
      suppressContentEditableWarning
    >
      {value || placeholder}
    </div>
  );
};

interface SlideContent {
  headline?: string;
  statement?: string;
  highlights?: string | string[];
  bgColor?: string;
  emojis?: string[];
  onEdit?: (content: SlideContent) => void;
}

const IntroStatementSlide4 = {
  id: "intro-statement4-slide",
  name: "Introductory Statement Slide (Geometric)",
  fields: [
    {
      id: "headline",
      label: "Headline or Question",
      type: "text",
    },
    {
      id: "statement",
      label: "Introductory Statement",
      type: "text",
    },
    {
      id: "highlights",
      label: "Key Highlights (comma separated)",
      type: "text",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
    },
    {
      id: "emojis",
      label: "Emojis",
      type: "array",
      defaultValue: ["🎯", "📊", "🎨"],
    },
  ],
  render: (content: SlideContent) => {
    const {
      headline = "",
      statement = "",
      highlights = "",
      bgColor,
      emojis = ["🎯", "📊", "🎨"],
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      // Update the configuration in PreviewSlides
      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const highlightArray = Array.isArray(highlights)
      ? highlights
      : highlights
        ? highlights.split(",").map((h: string) => h.trim())
        : [];

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Geometric Pattern Background */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute transform rotate-45"
              style={{
                width: Math.random() * 200 + 100 + "px",
                height: Math.random() * 200 + 100 + "px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                left: Math.random() * 100 + "%",
                top: Math.random() * 100 + "%",
                animation: `float-${i} ${Math.random() * 10 + 20}s linear infinite`,
              }}
            />
          ))}
        </div>

        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40" />

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-16 py-12">
          <div className="max-w-5xl mx-auto text-center">
            {/* Headline Container */}
            <div className="relative mb-12">
              {/* Decorative Icon */}
              <div className="mb-8">
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-white/20 rounded-full blur-xl"></div>
                  <div className="relative w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <span className="text-4xl">{emojis[0] || "🎯"}</span>
                  </div>
                </div>
              </div>

              {/* Headline */}
              <EditableDiv
                text={headline}
                onEdit={handleEdit("headline")}
                className="text-6xl font-black mb-4 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/70"
                placeholder="Enter headline..."
              />
            </div>

            {/* Main Statement */}
            <div className="relative mb-16">
              <div className="absolute -inset-4 bg-white/5 rounded-2xl blur-xl"></div>
              <EditableDiv
                text={statement}
                onEdit={handleEdit("statement")}
                className="relative text-3xl font-light leading-relaxed text-white/90"
                placeholder="Enter statement..."
              />
            </div>

            {/* Highlights Grid */}
            {highlightArray.length > 0 && (
              <div className="grid grid-cols-3 gap-8 mt-8">
                {highlightArray.map((highlight, index) => (
                  <div key={index} className="group relative">
                    <div className="absolute -inset-2 bg-white/5 rounded-lg blur-sm transition-all duration-300 group-hover:bg-white/10"></div>
                    <div className="relative bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/10 transition-transform duration-300 hover:scale-105">
                      {/* Highlight Icon */}
                      <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                        <span className="text-3xl">
                          {emojis[index] || "🎯"}
                        </span>
                      </div>
                      <EditableDiv
                        text={highlight}
                        onEdit={(value) => {
                          const newHighlights = [...highlightArray];
                          newHighlights[index] = value;
                          handleEdit("highlights")(newHighlights.join(", "));
                        }}
                        className="text-lg font-medium text-white/90"
                        placeholder={`Enter highlight ${index + 1}...`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
};

export default IntroStatementSlide4;
